import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@material-ui/core';
import SupervisedUserCircle from '@material-ui/icons/SupervisedUserCircle';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../models/Redux';
import { useStyles } from './styles';
import Intro from './parts/Intro';
import LoaderComponent from '../../components/Loader';
import ButtonManager from './parts/ButtonManager';
import SocketIoManager from './parts/SocketIoManager';
import ChatInput from './parts/ChatInput';
import Chat from './parts/Chat';
import StreamingManager from './parts/StreamingManager';
import MobileVideoLayout from './parts/MobileVideoLayout';
import RaiseHandQueue from './parts/RaiseHandQueue';
import {
    SET_EVENT_UUID,
    SET_LOCAL_STREAMING_STATE,
    thunkGetCurrentEvent,
} from '../../store/actions/Streaming.actions';
import { useResponsiveSize } from '../../config/hooks/useResponsiveSize';
import { UIBreakPoint, DeviceSensitive } from './config';
import DesktopVideoLayout from './parts/DesktopVideoLayout';

import microIcon from '../../assets/streaming/actions/dark/ic-streaming-mic-dark.svg';
import cameraIcon from '../../assets/streaming/actions/dark/ic-camera-on.svg';
import LocalVideoPreview from './parts/LocalVideoPreview/LocalVideoPreview';
import useVideoContext from './hooks/useVideoContext/useVideoContext';
import LocalAudioPreview from './parts/LocalAudioPreview/LocalAudioPreview';
import ParticipantListDialog from './parts/RaiseHandQueue/parts/ParticipantListDialog';
import chatIcon from '../../assets/streaming/actions/white/chat-bubble-white.svg';
import chatOffIcon from '../../assets/streaming/actions/white/chat-bubble-white-off.svg';
import Header from '../../parts/Header';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const StreamingWEBRTC: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const isMobileUI = useResponsiveSize(UIBreakPoint, DeviceSensitive);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    // @ts-ignore
    const { UUID } = useParams();
    const dispatch = useDispatch();

    const {
        user: { user_ide: userIde, user_username: userUsername },
        loading,
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const {
        eventData: {
            user: { user_ide: influencerUserIde },
        },
        loading: loadingStreaming,
        streamingFlags: { started },
        localStreamingState,
        streamingState: { current_state: currentState },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const isAdmin = useMemo(() => {
        return !!currentState?.admin_users?.find((username: string) => username === userUsername);
    }, [currentState, userUsername]);

    const { getAudioAndVideoTracks } = useVideoContext();

    useEffect(() => {
        dispatch({ type: SET_EVENT_UUID, payload: UUID });
        dispatch(thunkGetCurrentEvent(UUID));
    }, [UUID, dispatch]);

    useEffect(() => {
        if (localStreamingState.settingsModal) {
            getAudioAndVideoTracks().catch(error => {
                console.dir(error);
            });
        }
    }, [getAudioAndVideoTracks, localStreamingState.settingsModal]);

    const [value, setValue] = useState(0);

    const handleChange = (event: ChangeEvent, newValue: number): void => {
        setValue(newValue);
    };

    const renderSettingsDialog = () => {
        return (
            <Dialog
                open={localStreamingState.settingsModal}
                onClose={() => {
                    dispatch({
                        type: SET_LOCAL_STREAMING_STATE,
                        payload: {
                            ...localStreamingState,
                            settingsModal: false,
                        },
                    });
                }}
                scroll="paper"
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle>{t('Streaming Settings')}</DialogTitle>
                <DialogContent dividers className={classes.settingsTabsContainer}>
                    <Grid container xs={12} direction="row">
                        <Grid item xs={3}>
                            <Tabs
                                // @ts-ignore
                                onChange={handleChange}
                                orientation="vertical"
                                variant="scrollable"
                                value={value}
                                aria-label="Vertical tabs example"
                                className={classes.tabsContainer}
                                classes={{ indicator: classes.tabsIndicator }}
                                sx={{ borderRight: 1, borderColor: 'divider', backgroundColor: 'grey' }}
                            >
                                <Tab
                                    fullWidth
                                    classes={{ selected: classes.tabSelected }}
                                    label={
                                        <Grid container direction="row">
                                            <img
                                                src={cameraIcon}
                                                alt={t('Video settings')}
                                                className={classes.tabIcon}
                                            />
                                            <Typography className={classes.tabLabel}>{t('Video')}</Typography>
                                        </Grid>
                                    }
                                    {...a11yProps(0)}
                                />
                                <Tab
                                    fullWidth
                                    classes={{ selected: classes.tabSelected }}
                                    label={
                                        <Grid container direction="row">
                                            <img
                                                src={microIcon}
                                                alt={t('Audio settings')}
                                                className={classes.tabIcon}
                                            />
                                            <Typography className={classes.tabLabel}>{t('Audio')}</Typography>
                                        </Grid>
                                    }
                                    {...a11yProps(1)}
                                />
                            </Tabs>
                        </Grid>
                        <Grid className={classes.tabsContentContainer} xs={9}>
                            <TabPanel value={value} index={0}>
                                <LocalVideoPreview />
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <LocalAudioPreview />
                            </TabPanel>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            dispatch({
                                type: SET_LOCAL_STREAMING_STATE,
                                payload: {
                                    ...localStreamingState,
                                    settingsModal: false,
                                },
                            });
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderUIComponents = () => {
        if (isMobileUI) {
            return (
                <>
                    {!started && <Intro mobileLayout />}
                    {started && <ButtonManager mobileLayout />}
                    {started && <ChatInput mobileLayout />}
                    {started && localStreamingState.showChat && <Chat enabled={started} mobileLayout />}
                    {started && (
                        <IconButton
                            className={classes.hideShowIconButton}
                            onClick={() => {
                                dispatch({
                                    type: SET_LOCAL_STREAMING_STATE,
                                    payload: {
                                        ...localStreamingState,
                                        showChat: !localStreamingState.showChat,
                                    },
                                });
                            }}
                        >
                            <img
                                src={localStreamingState.showChat ? chatOffIcon : chatIcon}
                                alt="show/hide chat"
                            />
                        </IconButton>
                    )}
                    {started && <MobileVideoLayout />}
                    {started && <RaiseHandQueue mobileLayout />}
                </>
            );
        }
        return (
            <Grid
                xs={12}
                container
                justify="center"
                alignContent="flex-start"
                alignItems="flex-start"
                direction="row"
                style={{ position: 'relative', marginTop: 50 }}
            >
                {renderSettingsDialog()}
                {isAdmin && !isEventInfluencer && false && (
                    <Grid
                        xs={12}
                        container
                        justify="flex-end"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                        style={{ position: 'absolute', bottom: '9vh', marginRight: '2.5vw' }}
                    >
                        <Tooltip
                            // @ts-ignore
                            title={t('You are an admin. Click here for more info')}
                        >
                            <Fab
                                style={{ backgroundColor: '#FF5A60' }}
                                aria-label="add"
                                onClick={() => {
                                    enqueueSnackbar(
                                        t(
                                            'You are an administrator for this event. You can check the viewers connected to the event, block them and remove their messages.',
                                        ),
                                        { variant: 'warning', autoHideDuration: 10000 },
                                    );
                                }}
                            >
                                <SupervisedUserCircle style={{ color: 'white', fill: 'white' }} />
                            </Fab>
                        </Tooltip>
                    </Grid>
                )}

                <Grid
                    md={isFullScreen ? 12 : 8}
                    lg={isFullScreen ? 12 : 9}
                    xl={isFullScreen ? 12 : 10}
                    container
                    className={classes.desktopVideoContainerWrapper}
                >
                    <Grid
                        xs={12}
                        container
                        justify="center"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                        className={clsx(
                            isFullScreen ? classes.fullScreenVideoContainer : classes.desktopVideoContainer,
                            !isFullScreen && !started ? classes.darkBackground : '',
                        )}
                    >
                        {!started && <Intro />}
                        {started && (
                            <DesktopVideoLayout
                                isFullScreen={isFullScreen}
                                setIsFullScreen={setIsFullScreen}
                            />
                        )}
                    </Grid>
                </Grid>
                <Grid
                    md={4}
                    lg={3}
                    xl={2}
                    container
                    justify="center"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                    className={classes.desktopVideoContainerWrapper}
                >
                    <Grid
                        xs={12}
                        container
                        justify="space-between"
                        alignContent="space-between"
                        alignItems="center"
                        direction="row"
                        className={classes.desktopChatContainerWrapper}
                    >
                        <Grid
                            xs={12}
                            container
                            justify="center"
                            alignContent="space-between"
                            alignItems="center"
                            direction="row"
                            className={classes.desktopChatContainer}
                        >
                            <Typography className={classes.header}>{t('Live Chat')}</Typography>
                            <Chat enabled={started} />
                            <ChatInput enabled={started} />
                        </Grid>
                    </Grid>
                </Grid>
                <ButtonManager />
            </Grid>
        );
    };

    if (loading || loadingStreaming) return <LoaderComponent fullPage />;

    return (
        <>
            <Header streamingPage />
            <Grid container xs={12} className={isMobileUI ? classes.container : classes.desktopContainer}>
                <SocketIoManager />
                <StreamingManager />
                {started && <ParticipantListDialog mobileLayout={isMobileUI} />}
                {renderUIComponents()}
            </Grid>
        </>
    );
};

export default StreamingWEBRTC;
